import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, ReferenceLine,
  PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList
} from 'recharts';
import { TrendingUp, Wallet, LineChart as LineChartIcon, ChevronLeft, ChevronRight, Scale, PieChart as PieIcon, Landmark, Plus, Trash2, Pencil } from 'lucide-react';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const CATEGORY_COLORS = ['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#14b8a6', '#64748b', '#ec4899', '#f97316', '#0ea5e9', '#a3a3a3'];

const SalesAnalysis = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [netView, setNetView] = useState('daily');
  const [svrView, setSvrView] = useState('daily');
  const { currencySymbol } = useCurrency();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  const [loanData, setLoanData] = useState(null);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [savingLoan, setSavingLoan] = useState(false);
  const todayIso = new Date().toISOString().split('T')[0];
  const [loanForm, setLoanForm] = useState({ type: 'received', amount: '', date: todayIso, lender: '', note: '' });

  const years = [];
  for (let y = now.getFullYear() - 3; y <= now.getFullYear() + 1; y++) years.push(y);

  useEffect(() => { fetchData(); fetchLoans(); /* eslint-disable-next-line */ }, [year, month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales-analysis?year=${year}&month=${month}`, getAuthHeader());
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load sales analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchLoans = async () => {
    try {
      const res = await axios.get(`${API}/company-loans?year=${year}&month=${month}`, getAuthHeader());
      setLoanData(res.data);
    } catch (e) {
      // non-blocking
    }
  };

  const openAddLoan = () => {
    setEditingLoan(null);
    setLoanForm({ type: 'received', amount: '', date: todayIso, lender: '', note: '' });
    setLoanDialogOpen(true);
  };

  const openEditLoan = (entry) => {
    setEditingLoan(entry);
    setLoanForm({ type: entry.type, amount: String(entry.amount), date: entry.date, lender: entry.lender || '', note: entry.note || '' });
    setLoanDialogOpen(true);
  };

  const saveLoan = async (e) => {
    e.preventDefault();
    const amount = parseFloat(loanForm.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    if (!loanForm.date) { toast.error('Select a date'); return; }
    setSavingLoan(true);
    try {
      const payload = { type: loanForm.type, amount, date: loanForm.date, lender: loanForm.lender, note: loanForm.note };
      if (editingLoan) {
        await axios.put(`${API}/company-loans/${editingLoan.id}`, payload, getAuthHeader());
        toast.success('Loan entry updated');
      } else {
        await axios.post(`${API}/company-loans`, payload, getAuthHeader());
        toast.success('Loan entry recorded');
      }
      setLoanDialogOpen(false);
      await Promise.all([fetchData(), fetchLoans()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save loan entry');
    } finally {
      setSavingLoan(false);
    }
  };

  const deleteLoan = async (id) => {
    if (!window.confirm('Delete this loan entry?')) return;
    try {
      await axios.delete(`${API}/company-loans/${id}`, getAuthHeader());
      toast.success('Loan entry deleted');
      await Promise.all([fetchData(), fetchLoans()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const fmtMoney = (n) => `${currencySymbol}${new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(Math.round(n || 0))}`;
  const compactMoney = (n) => {
    const v = Math.round(n || 0);
    if (Math.abs(v) >= 1000000) return `${currencySymbol}${(v / 1000000).toFixed(1)}M`;
    if (Math.abs(v) >= 1000) return `${currencySymbol}${(v / 1000).toFixed(0)}k`;
    return `${currencySymbol}${v}`;
  };

  const totals = data?.totals || {};
  const dayLabel = (iso) => (iso ? parseInt(iso.split('-')[2], 10) : '');

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const isDay = /^\d+$/.test(String(label));
    const title = isDay ? `${MONTHS[month - 1]} ${label}, ${year}` : `${label} ${year}`;
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2">
        <div className="text-xs font-semibold text-slate-700 mb-1">{title}</div>
        {payload.map((p, i) => (
          <div key={i} className="text-xs flex items-center gap-2" style={{ color: p.color }}>
            <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-slate-600">{p.name}:</span>
            <span className="font-semibold">{fmtMoney(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const statCards = [
    { label: 'Total Billing (Sales)', value: fmtMoney(totals.sales), color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
    { label: 'Total Revenue (Customer Payments + Loans)', value: fmtMoney(totals.revenue), color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    { label: 'Zenith Expenses', value: fmtMoney(totals.expenses), color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
    { label: 'Profit (Payments − Expenses)', value: fmtMoney(totals.profit), color: (totals.profit || 0) >= 0 ? 'text-violet-600' : 'text-red-600', bg: 'bg-violet-50', border: 'border-violet-200' },
  ];

  return (
    <div className="space-y-6" data-testid="sales-analysis-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shadow-lg">
            <LineChartIcon className="w-6 h-6 text-white" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Sales Analysis</h1>
            <p className="text-sm text-slate-600">Daily billing vs revenue, and running profit for the month</p>
          </div>
        </div>

        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <button onClick={() => changeMonth(-1)} data-testid="prev-month-btn"
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} data-testid="month-select"
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none cursor-pointer">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} data-testid="year-select"
            className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 outline-none cursor-pointer">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={() => changeMonth(1)} data-testid="next-month-btn"
            className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin w-9 h-9 border-4 border-emerald-500 border-t-transparent rounded-full" />
          <span className="ml-3 text-slate-500">Loading analysis…</span>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sales-stat-cards">
            {statCards.map((s, i) => (
              <div key={i} className={`card p-4 border ${s.border} ${s.bg}`}>
                <div className="text-xs font-medium text-slate-500">{s.label}</div>
                <div className={`text-2xl font-bold mt-1 truncate ${s.color}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Company Loans section */}
          <div className="card" data-testid="company-loans-panel">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Landmark className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Company Loans</h2>
                  <p className="text-xs text-slate-500">Loans received add to Revenue; repayments add to Zenith Expenses — {MONTHS[month - 1]} {year}</p>
                </div>
              </div>
              {isAdmin && (
                <Button onClick={openAddLoan} data-testid="add-loan-btn" className="bg-amber-600 hover:bg-amber-700 shrink-0">
                  <Plus className="w-4 h-4 mr-1.5" /> Add Loan Entry
                </Button>
              )}
            </div>

            {/* Loan summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="loan-month-received">
                <div className="text-xs font-medium text-slate-500">Loan Received ({MONTHS[month - 1]})</div>
                <div className="text-xl font-bold text-emerald-600 mt-1">{fmtMoney(loanData?.month_received)}</div>
              </div>
              <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="loan-month-repaid">
                <div className="text-xs font-medium text-slate-500">Loan Repaid ({MONTHS[month - 1]})</div>
                <div className="text-xl font-bold text-red-600 mt-1">{fmtMoney(loanData?.month_repaid)}</div>
              </div>
              <div className="rounded-xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4" data-testid="loan-balance">
                <div className="text-xs font-medium text-slate-500">Outstanding Loan Balance (all-time)</div>
                <div className="text-xl font-bold text-amber-700 mt-1">{fmtMoney(loanData?.balance)}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">Taken {fmtMoney(loanData?.total_received)} − Repaid {fmtMoney(loanData?.total_repaid)}</div>
              </div>
            </div>

            {/* Per-lender breakdown */}
            {loanData?.by_lender?.length > 0 && (
              <div className="mb-5" data-testid="loan-by-lender">
                <div className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Balance by Lender</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {loanData.by_lender.map((l) => (
                    <div key={l.lender} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`lender-${l.lender}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-800 truncate" title={l.lender}>{l.lender}</span>
                        <span className={`text-sm font-bold ${l.balance > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{fmtMoney(l.balance)}</span>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">Taken {fmtMoney(l.received)} − Repaid {fmtMoney(l.repaid)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Loan entries */}
            {loanData?.entries?.length ? (
              <div className="overflow-x-auto border border-slate-100 rounded-xl">
                <table className="w-full text-sm" data-testid="loan-entries-table">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs">
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">Type</th>
                      <th className="text-left font-medium px-3 py-2">Lender</th>
                      <th className="text-right font-medium px-3 py-2">Amount</th>
                      <th className="text-left font-medium px-3 py-2">Note</th>
                      {isAdmin && <th className="text-right font-medium px-3 py-2">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loanData.entries.map((e) => (
                      <tr key={e.id} className="border-t border-slate-100" data-testid={`loan-row-${e.id}`}>
                        <td className="px-3 py-2 text-slate-600">{e.date}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${e.type === 'received' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                            {e.type === 'received' ? 'Received' : 'Repayment'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{e.lender || '—'}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${e.type === 'received' ? 'text-emerald-600' : 'text-red-600'}`}>{fmtMoney(e.amount)}</td>
                        <td className="px-3 py-2 text-slate-500">{e.note || '—'}</td>
                        {isAdmin && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button onClick={() => openEditLoan(e)} data-testid={`edit-loan-${e.id}`} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => deleteLoan(e.id)} data-testid={`delete-loan-${e.id}`} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-400 py-6 text-sm">No loan entries for {MONTHS[month - 1]} {year}.</p>
            )}
          </div>

          {/* Feature 2: Daily/Monthly Sales vs Revenue */}
          <div className="card" data-testid="daily-sales-revenue-panel">
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">{svrView === 'monthly' ? 'Monthly' : 'Daily'} Sales vs Revenue</h2>
                  <p className="text-xs text-slate-500">
                    {svrView === 'monthly'
                      ? `Billing raised vs payments received each month — ${year}`
                      : `Billing raised vs payments received each day — ${MONTHS[month - 1]} ${year}`}
                  </p>
                </div>
              </div>
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 shrink-0" data-testid="svr-view-toggle">
                <button
                  onClick={() => setSvrView('daily')}
                  data-testid="svr-view-daily"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${svrView === 'daily' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Daily
                </button>
                <button
                  onClick={() => setSvrView('monthly')}
                  data-testid="svr-view-monthly"
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${svrView === 'monthly' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Monthly
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={(svrView === 'monthly' ? data?.monthly : data?.daily) || []} margin={{ top: 18, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                {svrView === 'monthly'
                  ? <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  : <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />}
                <YAxis tickFormatter={compactMoney} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={55} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(148,163,184,0.1)' }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sales" name={`${svrView === 'monthly' ? 'Monthly' : 'Daily'} Sales (Billing)`} fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={svrView === 'monthly' ? 34 : 28}>
                  <LabelList dataKey="sales" position="top" formatter={(v) => (v ? compactMoney(v) : '')} style={{ fill: '#3b82f6', fontSize: 10, fontWeight: 600 }} />
                </Bar>
                <Bar dataKey="revenue" name={`${svrView === 'monthly' ? 'Monthly' : 'Daily'} Revenue (Payments)`} fill="#10b981" radius={[6, 6, 0, 0]} maxBarSize={svrView === 'monthly' ? 34 : 28}>
                  <LabelList dataKey="revenue" position="top" formatter={(v) => (v ? compactMoney(v) : '')} style={{ fill: '#10b981', fontSize: 10, fontWeight: 600 }} />
                </Bar>
                <Bar dataKey="profit" name={`${svrView === 'monthly' ? 'Monthly' : 'Daily'} Profit`} fill="#8b5cf6" radius={[6, 6, 0, 0]} maxBarSize={svrView === 'monthly' ? 34 : 28}>
                  <LabelList dataKey="profit" position="top" formatter={(v) => (v ? compactMoney(v) : '')} style={{ fill: '#8b5cf6', fontSize: 10, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Feature 3: Running Payments, Expenses & Profit */}
          <div className="card" data-testid="profit-panel">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
                <Wallet className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Running Payments, Expenses & Profit</h2>
                <p className="text-xs text-slate-500">Cumulative customer payments vs Zenith Bank expenses, and the resulting profit — {MONTHS[month - 1]} {year}</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={data?.cumulative || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={compactMoney} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={55} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="payments" name="Payments (cumulative)" stroke="#10b981" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="expenses" name="Expenses (cumulative)" stroke="#ef4444" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="profit" name="Profit" stroke="#8b5cf6" strokeWidth={3} dot={false} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Billing minus Zenith Expenses */}
          <div className="card" data-testid="net-billing-panel">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                  <Scale className="w-5 h-5" />
                </span>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Billing − Zenith Expenses</h2>
                  <p className="text-xs text-slate-500">Total billing (sales) minus Zenith Bank payments/charges — {MONTHS[month - 1]} {year}</p>
                </div>
              </div>
              {/* Daily / Monthly toggle */}
              <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5" data-testid="net-view-toggle">
                {['daily', 'monthly'].map((v) => (
                  <button
                    key={v}
                    onClick={() => setNetView(v)}
                    data-testid={`net-view-${v}`}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${netView === v ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            {netView === 'monthly' ? (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center" data-testid="net-billing-monthly">
                {/* Breakdown */}
                <div className="md:col-span-2 space-y-3">
                  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-blue-50 border border-blue-200">
                    <span className="text-sm text-slate-600">Total Billing (Sales)</span>
                    <span className="text-lg font-bold text-blue-600">{fmtMoney(totals.sales)}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-red-50 border border-red-200">
                    <span className="text-sm text-slate-600">− Zenith Expenses</span>
                    <span className="text-lg font-bold text-red-600">{fmtMoney(totals.expenses)}</span>
                  </div>
                  <div className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 ${(totals.net_billing || 0) >= 0 ? 'bg-amber-50 border-amber-300' : 'bg-red-50 border-red-300'}`}>
                    <span className="text-sm font-semibold text-slate-700">= Net Billing</span>
                    <span className={`text-2xl font-extrabold ${(totals.net_billing || 0) >= 0 ? 'text-amber-600' : 'text-red-600'}`} data-testid="net-billing-monthly-value">{fmtMoney(totals.net_billing)}</span>
                  </div>
                </div>
                {/* Comparison bars */}
                <div className="md:col-span-3">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={[
                        { name: 'Billing', value: totals.sales || 0 },
                        { name: 'Expenses', value: totals.expenses || 0 },
                        { name: 'Net', value: totals.net_billing || 0 },
                      ]}
                      margin={{ top: 20, right: 20, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                      <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={compactMoney} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={55} />
                      <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                      <ReferenceLine y={0} stroke="#cbd5e1" />
                      <Bar dataKey="value" radius={[8, 8, 0, 0]} barSize={60}>
                        {['#3b82f6', '#ef4444', (totals.net_billing || 0) >= 0 ? '#f59e0b' : '#ef4444'].map((c, i) => <Cell key={i} fill={c} />)}
                        <LabelList dataKey="value" position="top" formatter={compactMoney} style={{ fill: '#334155', fontSize: 11, fontWeight: 600 }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={data?.daily || []} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={compactMoney} tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={55} />
                  <Tooltip
                    formatter={(v) => [fmtMoney(v), 'Billing − Expenses']}
                    labelFormatter={(d) => `${MONTHS[month - 1]} ${dayLabel(d)}, ${year}`}
                    contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                  />
                  <ReferenceLine y={0} stroke="#cbd5e1" />
                  <Bar dataKey="net_billing" name="Billing − Expenses" radius={[6, 6, 0, 0]} barSize={16}>
                    {(data?.daily || []).map((row, i) => (
                      <Cell key={i} fill={(row.net_billing || 0) >= 0 ? '#f59e0b' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Expenses by Category */}
          <div className="card" data-testid="expense-category-panel">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
                <PieIcon className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Expenses by Category</h2>
                <p className="text-xs text-slate-500">Where Zenith Bank money went — {MONTHS[month - 1]} {year}</p>
              </div>
            </div>
            {(!data?.expense_by_category || data.expense_by_category.length === 0) ? (
              <p className="text-sm text-slate-400 py-10 text-center">No categorized expenses this month. Tag payments on the Zenith Bank page to see the breakdown.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={data.expense_by_category}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%" cy="50%"
                      innerRadius={60} outerRadius={100}
                      paddingAngle={2}
                    >
                      {data.expense_by_category.map((c, i) => (
                        <Cell key={c.category} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [fmtMoney(v), n]} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2" data-testid="expense-category-list">
                  {data.expense_by_category.map((c, i) => (
                    <div key={c.category} className="flex items-center gap-3">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                      <span className="text-sm text-slate-700 flex-1 truncate" title={c.category}>{c.category}</span>
                      <span className="text-xs text-slate-400 w-12 text-right">{c.pct}%</span>
                      <span className="text-sm font-semibold text-slate-800 w-28 text-right">{fmtMoney(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Loan dialog */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="max-w-md" data-testid="loan-dialog">
          <DialogHeader>
            <DialogTitle>{editingLoan ? 'Edit Loan Entry' : 'Add Loan Entry'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveLoan} className="space-y-4">
            <div>
              <Label className="text-sm">Type</Label>
              <div className="inline-flex w-full mt-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                <button type="button" onClick={() => setLoanForm({ ...loanForm, type: 'received' })} data-testid="loan-type-received"
                  className={`flex-1 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${loanForm.type === 'received' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}>
                  Loan Received
                </button>
                <button type="button" onClick={() => setLoanForm({ ...loanForm, type: 'repayment' })} data-testid="loan-type-repayment"
                  className={`flex-1 px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ${loanForm.type === 'repayment' ? 'bg-white text-red-600 shadow-sm' : 'text-slate-500'}`}>
                  Loan Repayment
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="loan-amount" className="text-sm">Amount ({currencySymbol})</Label>
              <Input id="loan-amount" type="number" min="0" step="0.01" value={loanForm.amount}
                onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })}
                placeholder="0" data-testid="loan-amount-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="loan-date" className="text-sm">Date</Label>
              <Input id="loan-date" type="date" value={loanForm.date}
                onChange={(e) => setLoanForm({ ...loanForm, date: e.target.value })}
                data-testid="loan-date-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="loan-lender" className="text-sm">Lender</Label>
              <Input id="loan-lender" value={loanForm.lender}
                onChange={(e) => setLoanForm({ ...loanForm, lender: e.target.value })}
                placeholder="e.g. Zenith Bank, John the investor" data-testid="loan-lender-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="loan-note" className="text-sm">Note (optional)</Label>
              <Input id="loan-note" value={loanForm.note}
                onChange={(e) => setLoanForm({ ...loanForm, note: e.target.value })}
                placeholder="e.g. facility ref, purpose" data-testid="loan-note-input" className="mt-1" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={savingLoan} data-testid="save-loan-btn" className="flex-1 bg-amber-600 hover:bg-amber-700">
                {savingLoan ? 'Saving…' : (editingLoan ? 'Update Entry' : 'Save Entry')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setLoanDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesAnalysis;
